import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { TYPE_COLORS, TYPE_NAMES_RU, getDeviceStatus, getDeviceThermalPct } from './equipmentTypes';
import type { RackEquipmentItem } from './rackTypes';
import type { ProjectionMode } from '../equipment/RackCanvasToolbar';
import { normalizeMountingPosition } from '../../utils/halfDepth.logic';
import { resolveEquipmentTenant } from '../../utils/tenancyCascade.logic';

interface GridEquipmentProps {
  eq: RackEquipmentItem;
  uSlots: number;
  activeMode: 'inspect' | 'thermal' | 'cable';
  isMountingMode?: boolean;
  projectionMode: ProjectionMode;
  isDimmed?: boolean;
  hoveredEqId: string | null;
  setHoveredEqId: (id: string | null) => void;
  onClick?: (eq: RackEquipmentItem) => void;
  cablingDraft?: { sourceEqId: string; sourcePortName: string; sourcePortId: string; x: number; y: number } | null;
  onCablingDraftConnect?: (destEqId: string) => void;
  onMousePos?: (eq: RackEquipmentItem, clientX: number, clientY: number) => void;
  isDark?: boolean;
  liveMetrics?: {temp_c?: number, power_w?: number};
  /** PDU outlet summary — only passed when equipment_type === 'PDU' */
  pduSummary?: { free_outlets: number; total_outlets: number; name?: string } | null;
  /** Coexisting equipment installed on the opposite face in the same U */
  coexistingOpposite?: { name: string; mounting_position?: string; u_height?: number } | null;
  /** Tenant assigned to the parent rack cabinet */
  rackTenant?: string;
}

/**
 * One equipment block rendered inside the rack grid.
 * Handles dragging (dnd-kit), hover state, thermal overlay, and status dots.
 */
export function GridEquipment({
  eq,
  uSlots,
  activeMode,
  isMountingMode,
  projectionMode,
  isDimmed,
  hoveredEqId,
  setHoveredEqId,
  onClick,
  cablingDraft,
  onCablingDraftConnect,
  onMousePos,
  isDark = true,
  liveMetrics,
  pduSummary,
  coexistingOpposite,
  rackTenant,
}: GridEquipmentProps) {
  const tenantRes = resolveEquipmentTenant(
    eq.tenant || (eq.attributes as any)?.tenant,
    rackTenant
  );

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: eq.id,
    data: { equipment: eq },
    disabled: !isMountingMode,
  });

  const u_pos    = Number(eq.u_position);
  const u_h      = Number(eq.u_height);
  const total_u  = Number(uSlots);
  const rowStart = total_u - u_pos - u_h + 2;

  const normalizedMount = normalizeMountingPosition(eq.mounting_position);

  let colStart = '2 / 3';
  if (projectionMode === 'split') {
    colStart = normalizedMount === 'full_depth' ? '2 / 5' : (normalizedMount === 'rear' ? '4 / 5' : '2 / 3');
  }

  const isIsometric = projectionMode === 'isometric';
  const isFullDepth  = normalizedMount === 'full_depth';

  const bgColor    = TYPE_COLORS[eq.equipment_type] || TYPE_COLORS.unknown;
  const status     = getDeviceStatus(eq.status);
  
  // Use real metrics if available, otherwise fallback to mock
  const realTemp = liveMetrics?.temp_c;
  const realPower = liveMetrics?.power_w;
  const thermalPct = realTemp ? Math.min((realTemp / 80) * 100, 100) : getDeviceThermalPct(eq);

  const is1U = eq.u_height === 1;

  const style: React.CSSProperties = {
    gridRow:    `${rowStart} / span ${eq.u_height}`,
    gridColumn: colStart,
    zIndex:     isDragging ? 50 : 10,
    opacity:    isDragging
      ? 0.8
      : (isDimmed ? 0.2 : (hoveredEqId && hoveredEqId !== eq.id ? 0.45 : 1)),
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : (isIsometric ? (isFullDepth ? 'translateZ(10px)' : 'translateZ(0)') : undefined),
    transition: 'opacity 0.15s ease',
    borderLeft: `3px solid ${bgColor}`,
  };

  let borderStyle    = isDark ? 'border-slate-600' : 'border-gray-400';
  let thermalOverlay = '';

  if (activeMode === 'thermal') {
    if (thermalPct >= 80) {
      borderStyle    = 'border-red-500 shadow-[0_0_12px_#ef4444]';
      thermalOverlay = 'bg-gradient-to-r from-red-600/40 via-amber-600/30 to-red-600/40 animate-pulse';
    } else if (thermalPct >= 50) {
      borderStyle    = 'border-amber-500 shadow-[0_0_8px_#f59e0b]';
      thermalOverlay = 'bg-gradient-to-r from-amber-600/30 via-yellow-600/20 to-amber-600/30';
    } else {
      borderStyle    = 'border-cyan-500/50 shadow-[0_0_6px_#06b6d4]';
      thermalOverlay = 'bg-gradient-to-r from-cyan-600/20 via-blue-600/10 to-cyan-600/20';
    }
  }

  const isCableTarget = cablingDraft && cablingDraft.sourceEqId !== eq.id;
  const isHovered     = hoveredEqId === eq.id;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(isMountingMode ? listeners : {})}
      onClick={(e) => {
        e.stopPropagation();
        if (isCableTarget) {
          onCablingDraftConnect?.(eq.id);
        } else {
          onClick?.(eq);
        }
      }}
      onMouseEnter={(e) => {
        setHoveredEqId(eq.id);
        onMousePos?.(eq, e.clientX, e.clientY);
      }}
      onMouseMove={(e) => {
        onMousePos?.(eq, e.clientX, e.clientY);
      }}
      onMouseLeave={() => setHoveredEqId(null)}
      className={`relative rounded-sm mx-[2px] border overflow-hidden ${borderStyle} ${
        is1U ? 'py-0 px-2 flex flex-row items-center' : 'p-1.5 flex flex-col justify-between'
      } font-mono cursor-pointer transition-[border-color,box-shadow,opacity] group select-none min-w-0 active:scale-[0.98] active:brightness-90 ${
        isHovered ? 'border-cyan-400 shadow-[0_0_10px_rgba(6,182,212,0.35)]' : 'hover:border-cyan-500/70'
      } ${isDragging ? 'shadow-[0_0_20px_rgba(0,210,255,0.8)] border-cyan-300 z-50' : ''} ${
        isCableTarget ? 'animate-bounce border-purple-400 shadow-[0_0_12px_rgba(168,85,247,0.6)]' : ''
      } ${isIsometric ? 'shadow-[4px_4px_12px_rgba(0,0,0,0.6)]' : ''}`}
      role={!isMountingMode ? 'button' : undefined}
      tabIndex={!isMountingMode ? 0 : undefined}
      aria-label={eq.name}
      onKeyDown={!isMountingMode ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (isCableTarget) {
            onCablingDraftConnect?.(eq.id);
          } else {
            onClick?.(eq);
          }
        }
      } : undefined}
    >
      {/* Color overlay */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity"
        style={{ backgroundColor: bgColor, opacity: isHovered ? (isDark ? 0.2 : 0.3) : (isDark ? 0.08 : 0.2) }}
      />

      {/* Thermal overlay gradient & SVG */}
      {thermalOverlay && (
        <>
          <div className={`absolute inset-0 pointer-events-none ${thermalOverlay}`} />
          {realPower !== undefined && realPower > 0 && (
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none opacity-30"
              preserveAspectRatio="none"
              viewBox="0 0 100 100"
            >
              <path
                className="animate-[wave_3s_ease-in-out_infinite]"
                d="M0,50 Q25,25 50,50 T100,50 L100,100 L0,100 Z"
                fill={thermalPct >= 80 ? '#ef4444' : thermalPct >= 50 ? '#f59e0b' : '#06b6d4'}
              />
              <path
                className="animate-[wave_4s_ease-in-out_infinite_reverse] opacity-50"
                d="M0,60 Q25,40 50,60 T100,60 L100,100 L0,100 Z"
                fill={thermalPct >= 80 ? '#ef4444' : thermalPct >= 50 ? '#f59e0b' : '#06b6d4'}
              />
            </svg>
          )}
        </>
      )}

      {/* Name + position */}
      <div className={`flex justify-between items-center z-10 ${is1U ? 'flex-1 min-w-0 mr-3' : 'w-full'}`}>
        <span className={`text-[11px] font-bold truncate ${is1U ? 'max-w-[55%]' : 'max-w-[60%]'} ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
          {eq.name}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {tenantRes.effectiveTenant && tenantRes.effectiveTenant !== 'mine' && (
            <span
              data-testid="equipment-tenant-badge"
              title={
                tenantRes.isOverridden
                  ? `Владелец: ${tenantRes.effectiveTenant} (Индивидуальное переопределение)`
                  : `Владелец: ${tenantRes.effectiveTenant} (Унаследовано от стойки)`
              }
              className={`text-[8px] font-mono font-medium px-1 rounded-[2px] border truncate max-w-[80px] ${
                tenantRes.isOverridden
                  ? isDark
                    ? 'bg-amber-950/90 text-amber-300 border-amber-600/50'
                    : 'bg-amber-100 text-amber-900 border-amber-300'
                  : isDark
                    ? 'bg-purple-950/90 text-purple-300 border-purple-600/50'
                    : 'bg-purple-100 text-purple-900 border-purple-300'
              }`}
            >
              {tenantRes.isOverridden ? `★ ${tenantRes.effectiveTenant}` : tenantRes.effectiveTenant}
            </span>
          )}
          {coexistingOpposite && (() => {
            const isOppositeFront = projectionMode === 'rear' || normalizeMountingPosition(coexistingOpposite.mounting_position) === 'front';
            const badgeLabel = isOppositeFront ? '+ФРОНТ' : '+ТЫЛ';
            const badgeTitle = isOppositeFront
              ? `На лицевой панели этого слота установлено: ${coexistingOpposite.name} (${coexistingOpposite.u_height || 1}U)`
              : `В тылу этого слота установлено: ${coexistingOpposite.name} (${coexistingOpposite.u_height || 1}U)`;
            return (
              <span
                title={badgeTitle}
                className={`text-[8px] font-mono font-bold px-1 rounded-[2px] border ${
                  isOppositeFront
                    ? isDark
                      ? 'bg-cyan-950/90 text-cyan-300 border-cyan-600/50'
                      : 'bg-cyan-100 text-cyan-800 border-cyan-300'
                    : isDark
                      ? 'bg-indigo-950/90 text-indigo-300 border-indigo-600/50'
                      : 'bg-indigo-100 text-indigo-800 border-indigo-300'
                }`}
              >
                {badgeLabel}
              </span>
            );
          })()}
          <span className={`text-[9px] font-mono uppercase px-1 rounded-sm border ${
            isDark ? 'text-slate-300 bg-slate-950/90 border-slate-700' : 'text-gray-600 bg-white border-gray-300'
          }`}>
            U{eq.u_position}{!is1U && `·${eq.u_height}U`}
          </span>
        </div>
      </div>

      {/* Type + status/thermal badge + PDU outlets */}
      <div className={`flex items-center justify-between z-10 ${is1U ? 'shrink-0 gap-3' : 'w-full mt-auto'}`}>
        <span className={`text-[9px] uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
          {TYPE_NAMES_RU[eq.equipment_type] || eq.equipment_type}
        </span>
        {/* PDU outlet badge */}
        {pduSummary && (
          <span
            aria-label={`PDU: ${pduSummary.free_outlets} из ${pduSummary.total_outlets} свободно`}
            className={`text-[8px] font-bold px-1 rounded-sm tabular-nums ${
              pduSummary.free_outlets === 0
                ? 'bg-red-950/80 text-red-300'
                : pduSummary.free_outlets <= 2
                ? 'bg-amber-950/80 text-amber-300'
                : 'bg-emerald-950/80 text-emerald-300'
            }`}
          >
            {pduSummary.free_outlets}/{pduSummary.total_outlets}
          </span>
        )}
        {activeMode === 'thermal' ? (
          <div className="flex gap-1 items-center">
            {realPower !== undefined && (
              <span className={`text-[9px] font-bold px-1 rounded-sm ${isDark ? 'text-blue-300 bg-slate-950/80' : 'text-blue-900 bg-white/90'}`}>
                {realPower}W
              </span>
            )}
            <span className={`text-[9px] font-bold px-1 rounded-sm ${isDark ? 'text-amber-300 bg-slate-950/80' : 'text-amber-900 bg-white/90'}`}>
              {realTemp !== undefined ? `${realTemp.toFixed(1)}°C` : `${thermalPct}%`}
            </span>
          </div>
        ) : (
          <div className="flex items-center">
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                status === 'critical' ? 'bg-red-500 animate-ping' :
                status === 'warning'  ? 'bg-amber-500' : 'bg-emerald-400'
              }`}
              aria-label={`Статус: ${
                status === 'critical' ? 'критический' :
                status === 'warning'  ? 'предупреждение' : 'норма'
              }`}
              role="img"
            />
          </div>
        )}
      </div>
    </div>
  );
}
